import { ApiFetch } from "../api/base";
import { confirmDraft, FileUploadResult, uploadDraft, uploadFile } from "../api/resources";

/**
 * The draft upload: the clip goes up as soon as it is dropped, while it is analysed on this device and the
 * technique is chosen, so the server has cut it and stored its frames by the time Upload is pressed. Upload then
 * only confirms it. Anything that goes wrong with the draft falls back to the ordinary upload.
 */
export interface DraftUpload {
  /** The draft's upload id; null when the draft could not be made. Never rejects. */
  uploadId: Promise<number | null>;
  /** True once the draft is known to have failed: Upload then sends the clip straight away. */
  failed(): boolean;
  /** How much of the clip has been sent, 0..1. */
  fraction(): number;
  /** Told how much has been sent from now on (and straight away, what has been sent so far). */
  follow(onProgress: ((fraction: number) => void) | undefined): void;
}

export interface UploadArgs {
  file: File;
  sportId: number;
  videoDurationSec: number;
  /** A trimmed clip's window start (whole seconds); videoDurationSec is then its stop. */
  startSec?: number;
  keypointsJson?: string;
  analysisMode?: "free" | "premium";
  onProgress?: (fraction: number) => void;
}

const firstId = (res: FileUploadResult | null | undefined): number | null => {
  const ids = res?.newRecordIds ?? res?.NewRecordIds ?? [];
  return ids.length > 0 && ids[0] != null ? ids[0] : null;
};

export function startDraft(apiFetch: ApiFetch, file: File, videoDurationSec: number, startSec = 0): DraftUpload {
  let sent = 0;
  let failed = false;
  let listener: ((fraction: number) => void) | undefined;
  let uploadId: Promise<number | null>;
  try {
    uploadId = uploadDraft(apiFetch, {
      file,
      videoDurationSec,
      startSec,
      onProgress: (fraction) => {
        sent = fraction;
        listener?.(fraction);
      },
    })
      .then(firstId)
      .catch(() => null)
      .then((id) => {
        failed = id == null;
        return id;
      });
  } catch {
    // A draft must never break the page: no draft, and Upload sends the clip as before.
    failed = true;
    uploadId = Promise.resolve(null);
  }
  return {
    uploadId,
    failed: () => failed,
    fraction: () => sent,
    follow: (onProgress) => {
      listener = onProgress;
      onProgress?.(sent);
    },
  };
}

/** Upload pressed: confirm the draft when there is one, else (or when it failed) upload the clip as before. The
 *  upload's id, as the session page knows it. */
export async function submitUpload(apiFetch: ApiFetch, draft: DraftUpload | null, args: UploadArgs): Promise<number> {
  if (draft && !draft.failed()) {
    draft.follow(args.onProgress);
    const draftId = await draft.uploadId;
    draft.follow(undefined);
    if (draftId != null) {
      args.onProgress?.(1);
      try {
        const confirmed = firstId(await confirmDraft(apiFetch, {
          uploadId: draftId,
          sportId: args.sportId,
          videoDurationSec: args.videoDurationSec,
          startSec: args.startSec,
          keypointsJson: args.keypointsJson,
          analysisMode: args.analysisMode,
        }));
        if (confirmed != null) return confirmed;
      } catch {
        // falls back to the ordinary upload below
      }
    }
  }
  const uploaded = firstId(await uploadFile(apiFetch, args));
  if (uploaded == null) throw new Error("Upload succeeded but no upload id was returned.");
  return uploaded;
}
